import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'
import RelatedTreatments from '../components/related-treatments'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '45 minutes',
  session: '1-3 sessions',
  results: 'Results 6-8 weeks (permanent)',
  cost: 'From £99',
  downtime: 'Minimal',
}

const relatedTreatments = [
  {
    name: 'Dermaplaning Facial Cheshunt',
    slug: 'dermaplaning',
    description:
      'Pair body contouring with a glowing complexion using our dermaplaning facials.',
  },
  {
    name: 'Dermal Fillers Cheshunt',
    slug: 'dermal-fillers-cheshunt',
    description:
      'Enhance facial contours and restore youthful volume after shaping your body.',
  },
  {
    name: 'Botox in Cheshunt',
    slug: 'botox-cheshunt',
    description:
      'Complete your transformation with wrinkle-smoothing Botox treatments for the face.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much does fat dissolving cost in Cheshunt?',
    answer:
      'Fat dissolving treatments at Ada Aesthetics in Cheshunt start from £99 for Lemon Bottle injections. Larger areas cost £285 for 30ml. We also offer non-surgical liposuction packages from £199 per session or 3 visits with fat dissolving for £550. Book a free consultation at our Cheshunt clinic to receive a personalized quote based on your treatment areas.',
  },
  {
    question: 'Does the fat come back?',
    answer:
      'Good news! The fat cells are permanently destroyed, so expect long-lasting results if you keep up the diet and exercise. Fat dissolving injections work with your body: after the excess fat cells are dissolved, they are expelled naturally via the lymphatic system.',
  },
  {
    question: 'Liposuction vs fat freezing vs fat dissolving injections',
    answer:
      'Aqualyx isn’t designed for larger areas like love handles because ' +
      'multiple injections can lead to weeks of swelling. ' +
      'Think of Aqualyx as fat removal and body shaping rather than weight loss. ' +
      'For larger areas, consider fat freezing or liposuction.',
  },
  {
    question: 'How does Aqualyx work?',
    answer:
      'Aqualyx injections only take about 15 minutes but allow extra time for your ' +
      'Ada Aesthetics consultation. A water-based solution is injected into ' +
      'small areas of unwanted fat, which permanently destroys your fat cells. ' +
      'Most clients notice a difference within six weeks, but you may need a ' +
      'course, four weeks apart, for full results.',
  },
  {
    question: 'Does Aqualyx hurt?',
    answer:
      'The injections contain a local anaesthetic, and many people find the ' +
      'procedure more comfortable than expected. If you are needle-phobic,' +
      ' your practitioner can also apply numbing cream beforehand.' +
      ' Expect some discomfort afterwards, especially redness and swelling;' +
      ' swelling can last four weeks. ',
  },
]

const FatDissolving: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Fat Dissolving Injections in Cheshunt',
    'Professional fat dissolving treatments in Cheshunt, Hertfordshire. Lemon Bottle and Aqualyx injections to reduce stubborn fat. Non-surgical body contouring. Serving Cheshunt and surrounding areas.',
    '99'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Fat Dissolving Cheshunt', url: 'https://adaaesthetics.uk/fat-dissolving' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Fat Dissolving Injections Cheshunt | Lemon Bottle | Ada Aesthetics"
        description="Professional fat dissolving injections in Cheshunt from £99. Lemon Bottle & Aqualyx treatments. Non-surgical body contouring. Book consultation: 07478705252"
        keywords="fat dissolving Cheshunt, lemon bottle, aqualyx, body contouring, fat removal Hertfordshire"
        url="https://adaaesthetics.uk/fat-dissolving"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax titleName="Fat Dissolving Injections" customClass="page_title" />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Melt Away Stubborn Fat Forever with Fat Dissolving Injections in Cheshunt</h1>
              <p className="excerpt">
                Frustrated by that double chin, bingo wings, or muffin top that won't budge no matter how much you diet or exercise? Fat dissolving injections at Ada Aesthetics in Cheshunt permanently destroy stubborn fat cells—no surgery, no liposuction, just smoother contours and renewed body confidence. Say goodbye to the bulges holding you back. From £99. Serving Cheshunt, Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>

              <h2>What Are Fat Dissolving Injections?</h2>
              <p>
                If you are losing body confidence, fat-dissolving injections can
                smooth away bulges without liposuction. At Ada Aesthetics in Cheshunt, we are proud to offer our clients permanent
                fat loss without surgery using Aqualyx and Lemon Bottle fat dissolving injections.
                This safe body shaping treatment sculpts and tightens the face
                and body by permanently dissolving stubborn areas of
                fat that resist diet and exercise.
              </p>

              <h2>How Do Fat Dissolving Injections Work?</h2>
              <p>
                Fat dissolving injections use a water-based solution that is injected into
                small areas of unwanted fat, which permanently destroys your fat cells.
                The destroyed fat cells are then naturally expelled via your lymphatic system.
                Most clients notice a difference within six weeks, but you may need a
                course of treatments, four weeks apart, for full results.
              </p>

              <h2>What Areas Can Be Treated?</h2>
              <p>Aqualyx and Lemon Bottle can treat the following areas:</p>
              <ul>
                <li><strong>Double chin</strong> - reduce submental fat</li>
                <li><strong>Bingo wings</strong> - smooth upper arm fat</li>
                <li><strong>Armpits</strong> - reduce excess fat deposits</li>
                <li><strong>Cellulite</strong> - improve skin texture</li>
                <li><strong>Saddlebags</strong> - contour outer thighs</li>
                <li><strong>Knees</strong> - reduce fat around knees</li>
                <li><strong>Ankles</strong> - contour lower legs</li>
                <li><strong>Stomach</strong> - target stubborn belly fat</li>
              </ul>

              <h2>Fat Dissolving vs Other Body Contouring Methods</h2>
              <p>
                Aqualyx isn't designed for larger areas like love handles because
                multiple injections can lead to weeks of swelling.
                Think of Aqualyx as fat removal and body shaping rather than weight loss.
                For larger areas, consider fat freezing (cryolipolysis) or liposuction.
              </p>

              <h2>Your Fat Dissolving Treatment Journey</h2>
              <p>
                Aqualyx injections only take about 15 minutes but allow extra time for your
                Ada Aesthetics consultation. The injections contain a local anaesthetic, and many people find the
                procedure more comfortable than expected. If you are needle-phobic,
                your practitioner can also apply numbing cream beforehand.
              </p>
              <p>
                Expect some discomfort afterwards, especially redness and swelling;
                swelling can last four weeks. The fat cells are permanently destroyed, so expect long-lasting results if you keep up the diet and exercise.
              </p>

              <h2>Why Choose Ada Aesthetics for Fat Dissolving in Cheshunt?</h2>
              <ul>
                <li><strong>Expert practitioners</strong> trained in body contouring techniques</li>
                <li><strong>Permanent fat cell destruction</strong> - results that last</li>
                <li><strong>Non-surgical solution</strong> - no downtime or recovery</li>
                <li><strong>Multiple treatment options</strong> - Lemon Bottle and Aqualyx</li>
                <li><strong>Competitive pricing</strong> from £99</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
              </ul>

              <h2>Book Your Fat Dissolving Consultation in Cheshunt</h2>
              <p>
                Ready to target stubborn fat areas without surgery? Join clients who trust us for effective body contouring.
                Schedule your free consultation today - our experienced team will assess your treatment areas and create a
                personalized plan to help you achieve your body goals with Lemon Bottle or Aqualyx treatments.
              </p>
              <p>
                <strong>Book today to secure your appointment</strong> - Call us at <strong>07478705252</strong> or reserve your consultation online.
                Your complimentary assessment (worth £50) includes body analysis and customized treatment recommendations.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Fat Dissolving Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>
          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default FatDissolving

export const pageQuery = graphql`
  query FatDissolving {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
